import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import startCase from 'lodash/startCase';

import { Injectable } from '@angular/core';

import { ServerApi } from '@services/server-api';
import { Session } from '@services/session';
import { StatsigService } from '@services/statsig.service';
import { PendoAnalytics } from '@services/pendo-analytics';

import { getValueFromNamespace, setValueInNamespace } from '@lib/store2';
import { extractResponseBody } from '@lib/utils';
import { isDesktopView } from '@lib/screen-sizes';

import { ExpansionUpsellConfig, IExpansionUpsellsData, IServerUpsellInfo, IUpsellCTA, IUpsellInfo } from './interface';
import { IUpsellData } from '@app/admin/fleet-card/shared/nudge-upsells/interfaces';

export const EXPANSION_UPSELL_TYPES = {
  BANNER: 'sticky_banner',
  COMPANY_PAGE: 'company_page',
  FLOATING: 'floating',
  MODAL: 'modal',
  SIDEBAR: 'sidebar',
};

export const CTA_ACTION_TYPES = {
  redirect: 'redirect',
  generateLead: 'generate_lead',
  learnMore: 'learn_more',
  modal: 'modal',
};

export const ANY_INDUSTRY_KEY: string = 'any';

@Injectable({
  providedIn: 'root',
})
export class ExpansionUpsellService {
  private apiUrl = 'fleetcard/w1/company/upsell_info';

  private readonly featureFlag: string = 'expansion-upsells-enabled';
  private readonly dynamicConfigIDs: string[] = ['expansion-upsells-config', 'expansion-upsells-config-wave-2'];
  private readonly COMPANY_PAGE_DISMISSAL_THRESHOLD: number = 2;
  private readonly FLOATING_DISMISSAL_THRESHOLD: number = 3;
  private readonly STICKY_BANNER_DISMISSAL_THRESHOLD: number = 1;
  private readonly SIDEBAR_DISMISSAL_THRESHOLD: number = 2;
  private readonly PROMO_PLATFORM_DISMISSAL_THRESHOLD: number = 2;
  private readonly LOCAL_STORAGE_NAMESPACE: string = `USER_${this.session.getUserId()}.EXPANSION_UPSELLS`;

  private LOCALE_MAP: Record<string, string> = {
    'en-US': 'US',
    'fr-CA': 'CA',
    'es-MX': 'MX',
  };

  constructor(
    private api: ServerApi,
    private session: Session,
    private statsigService: StatsigService,
    private pendo: PendoAnalytics,
  ) { }

  public shouldShowAGUpsell(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): boolean {
    if (!isDesktopView()) return false;
    if (this.session.isDriver()) return false;
    if (!this.isFeatureFlagEnabled()) return false;

    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    let dismissalThreshold: number = this.COMPANY_PAGE_DISMISSAL_THRESHOLD;

    if (upsellType === EXPANSION_UPSELL_TYPES.BANNER) {
      if (!this.getDynamicConfig().is_sticky_banner_upsell_enabled) return false;
      dismissalThreshold = this.STICKY_BANNER_DISMISSAL_THRESHOLD;
    } else if (upsellType === EXPANSION_UPSELL_TYPES.COMPANY_PAGE) {
      if (!this.getDynamicConfig().is_company_page_upsell_enabled) return false;
    } else if (upsellType === EXPANSION_UPSELL_TYPES.FLOATING) {
      if (!this.getDynamicConfig().is_floating_upsell_enabled) return false;
      dismissalThreshold = this.FLOATING_DISMISSAL_THRESHOLD;
    }
    if (
      this.getCFS() < 1000 &&
      !this.isTrialCustomer() &&
      !this.hasAGs() &&
      !this.getUpsellDataFromNamespace(upsellKey).isCompleted &&
      this.getUpsellDataFromNamespace(upsellKey).dismissedCount < dismissalThreshold &&
      this.getUpsellDataFromNamespace(upsellKey).lastDismissedDate !== this.getCurrentDate()
    ) {
      return true;
    }
    return false;
  }

  public shouldShowFVCrossSell(): boolean {
    if (!isDesktopView()) return false;
    if (this.session.isDriver()) return false;
    if (!this.isFeatureFlagEnabled()) return false;
    if (!this.getDynamicConfig().is_fleet_view_cross_sell_enabled) return false;
    if (this.hasAGs() || this.session.hasCompanyVideoMonitoringEnabled()) return true;
    return false;
  }

  public shouldShowSidebarUpsell(id: string): boolean {
    if (this.getCFS() >= 1000 || this.isTrialCustomer() || !this.session.isAdmin()) return false;
    const upsellKey: string = this.getSidebarUpsellLocalStorageKey(id);
    return (
      !this.getUpsellDataFromNamespace(upsellKey).isCompleted &&
      this.getUpsellDataFromNamespace(upsellKey).dismissedCount < this.SIDEBAR_DISMISSAL_THRESHOLD &&
      this.getUpsellDataFromNamespace(upsellKey).lastDismissedDate !== this.getCurrentDate()
    );
  }

  public shouldShowPromoPlatformUpsell(id: string): boolean {
    const upsellKey: string = this.getPromoPlatformUpsellLocalStorageKey(id);
    return (
      this.getUpsellDataFromNamespace(upsellKey).dismissedCount <= this.PROMO_PLATFORM_DISMISSAL_THRESHOLD &&
      this.getUpsellDataFromNamespace(upsellKey).lastDismissedDate !== this.getCurrentDate()
    )
  }

  public getDynamicConfig(): IExpansionUpsellsData {
    const mergedUpsells: IExpansionUpsellsData = {
      upsells: [],
      is_sticky_banner_upsell_enabled: false,
      is_company_page_upsell_enabled: false,
      is_floating_upsell_enabled: false,
      is_fleet_view_cross_sell_enabled: false,
    };

    this.dynamicConfigIDs.forEach((configID: string) => {
      const config = this.statsigService.evaluateDynamicConfig(configID)?.value as unknown as IExpansionUpsellsData;

      if (config?.upsells?.length) {
        mergedUpsells.upsells.push(...config.upsells);
      }

      [
        'is_sticky_banner_upsell_enabled',
        'is_company_page_upsell_enabled',
        'is_floating_upsell_enabled',
        'is_fleet_view_cross_sell_enabled',
      ].forEach((key: string) => {
        if (key in config) {
          (mergedUpsells as IExpansionUpsellsData)[key] = (config as IExpansionUpsellsData)[key];
        }
      });
    });
    return mergedUpsells;
  }

  public isFeatureFlagEnabled(): boolean {
    return this.statsigService.evaluateGate(this.featureFlag);
  }

  private isTrialCustomer(): boolean {
    return this.session.isTrialCustomer();
  }

  private hasAGs(): boolean {
    return this.session.companyHasAssetGateways();
  }

  private getCFS(): number {
    return this.session.getCompositeFleetSize();
  }

  public getIndustry(): Observable<string[]> {
    return this.api.get<{ segments: string[] }>(this.apiUrl).pipe(
      map(extractResponseBody),
      map(response => response.segments),
    );
  }

  public getUpsellInfo(): Observable<IUpsellInfo> {
    return this.api.get<IServerUpsellInfo>(this.apiUrl).pipe(
      map(extractResponseBody),
      map((response: IServerUpsellInfo): IUpsellInfo => {
        const upsellInfo: IUpsellInfo = {
          segments: response.segments,
          upsells: response.upsells?.map(upsell => ({
            id: upsell.id,
            type: upsell.placements[0].type,
            header: upsell.placements[0].header,
            body: upsell.placements[0].body,
            backgroundImage: upsell.placements[0].background_image,
            minimizedImage: upsell.placements[0].minimized_image,
            modalConfig: {
              heading: upsell.placements[0].modal_config.heading,
              description: upsell.placements[0].modal_config.description,
              image: upsell.placements[0].modal_config.image,
              cta: upsell.placements[0].modal_config.cta,
            },
          })),
        };
        return upsellInfo;
      }),
    );
  }

  public getUpsellConfig(
    upsellType: keyof typeof EXPANSION_UPSELL_TYPES,
    industry: string,
  ): ExpansionUpsellConfig {
    const value: IExpansionUpsellsData = this.getDynamicConfig();

    return value.upsells.find((upsell: ExpansionUpsellConfig) => {
      return upsell.type === upsellType && upsell.industry === industry;
    });
  }

  public getUserLanguage(): string {
    return this.LOCALE_MAP[this.session.getUserLanguage()];
  }

  public markSidebarUpsellAsShown(id: string): void {
    const upsellKey: string = this.getSidebarUpsellLocalStorageKey(id);
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    if (upsellData.lastShownDate === this.getCurrentDate()) return;
    upsellData.shownCount += 1;
    upsellData.lastShownDate = this.getCurrentDate();
    this.setUpsellDataInNamespace(upsellKey, upsellData);
  }

  public markPromoPlatformUpsellAsShown(id: string): void {
    const upsellKey: string = this.getPromoPlatformUpsellLocalStorageKey(id);
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    if (upsellData.lastShownDate === this.getCurrentDate()) return;
    upsellData.shownCount += 1;
    upsellData.lastShownDate = this.getCurrentDate();
    this.setUpsellDataInNamespace(upsellKey, upsellData);
  }

  public markUpsellAsShown(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): void {
    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    if (upsellData.lastShownDate === this.getCurrentDate()) return;
    upsellData.shownCount += 1;
    upsellData.lastShownDate = this.getCurrentDate();
    this.setUpsellDataInNamespace(upsellKey, upsellData);
  }

  public getLastShownDate(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): string {
    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    return upsellData.lastShownDate;
  }

  public markSidebarUpsellAsCompleted(id: string): void {
    const upsellKey: string = this.getSidebarUpsellLocalStorageKey(id);
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    upsellData.isCompleted = true;
    upsellData.lastShownDate = this.getCurrentDate();
    this.setUpsellDataInNamespace(upsellKey, upsellData);
  }

  public markUpsellAsCompleted(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): void {
    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    const upsellData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    upsellData.isCompleted = true;
    upsellData.lastShownDate = this.getCurrentDate();
    this.setUpsellDataInNamespace(upsellKey, upsellData);
  }

  public markSidebarUpsellAsTemporarilyDismissed(id: string): void {
    const upsellKey: string = this.getSidebarUpsellLocalStorageKey(id);
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);
    dismissalData.lastDismissedDate = currentDate;
    this.setUpsellDataInNamespace(upsellKey, dismissalData);
  }

  public markSidebarUpsellAsDismissed(id: string): void {
    const upsellKey: string = this.getSidebarUpsellLocalStorageKey(id);
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);

    dismissalData.dismissedCount += 1;
    dismissalData.lastDismissedDate = currentDate;

    this.setUpsellDataInNamespace(upsellKey, dismissalData);
  }

  public markPromoPlatformUpsellAsDismissed(id: string): void {
    const upsellKey: string = this.getPromoPlatformUpsellLocalStorageKey(id);
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);

    dismissalData.dismissedCount += 1;
    dismissalData.lastDismissedDate = currentDate;

    this.setUpsellDataInNamespace(upsellKey, dismissalData);
  }

  public markUpsellAsDismissed(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): void {
    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);

    dismissalData.dismissedCount += 1;
    dismissalData.lastDismissedDate = currentDate;

    this.setUpsellDataInNamespace(upsellKey, dismissalData);
  }

  public markUpsellAsTemporarilyDismissed(upsellType: keyof typeof EXPANSION_UPSELL_TYPES, industry: string): void {
    const upsellKey: string = this.getUpsellLocalStorageKey(this.getUpsellConfig(upsellType, industry));
    const currentDate: string = this.getCurrentDate();
    const dismissalData: IUpsellData = this.getUpsellDataFromNamespace(upsellKey);

    dismissalData.lastDismissedDate = currentDate;

    this.setUpsellDataInNamespace(upsellKey, dismissalData);
  }

  private getUpsellLocalStorageKey(config: ExpansionUpsellConfig): string {
    return `expansion_upsell_${config.type}_${config.industry.replace(/\s/g, '')}`.toUpperCase();
  }

  public getSidebarUpsellLocalStorageKey(id: string): string {
    return `expansion_upsell_sidebar_${id}`.toUpperCase();
  }

  public getPromoPlatformUpsellLocalStorageKey(id: string): string {
    return `promo_platform_${id}`.toUpperCase();
  }

  public getUpsellDataFromNamespace(upsellKey: string): IUpsellData {
    return getValueFromNamespace(upsellKey, this.LOCAL_STORAGE_NAMESPACE) || {
      dismissedCount: 0,
      lastDismissedDate: '',
      shownCount: 0,
      lastShownDate: '',
      isCompleted: false,
    };
  }

  public setUpsellDataInNamespace(upsellKey: string, upsellData: IUpsellData): void {
    setValueInNamespace(upsellKey, upsellData, this.LOCAL_STORAGE_NAMESPACE);
  }

  public getCurrentDate(): string {
    const today: Date = new Date();
    return today.toISOString().split('T')[0];
  }

  private getEventName(
    upsellType: keyof typeof EXPANSION_UPSELL_TYPES,
    industry: string,
    variant: number | null = null,
    action: string | IUpsellCTA,
  ): string {
    const _action: string = typeof action === 'string' ? action as string : `${startCase((action as IUpsellCTA).text['US'])} Click - ${startCase((action as IUpsellCTA).action.type)}`;
    return `Expansion Upsell - ${startCase(upsellType)} - ${industry} ${variant ? `- Variant ${variant} -` : '-'} ${_action}`;
  }

  public trackEvent(
    upsellType: keyof typeof EXPANSION_UPSELL_TYPES,
    industry: string,
    variant: number | null = null,
    action: string | IUpsellCTA,
  ): void {
    this.pendo.trackEvent(this.getEventName(upsellType, industry, variant, action), {});
  }
}
